// Sample event data
// This is used so we will not display all hours.

import { EnrichedEventData, NoteData, OrderData, PrivateSlotOut, ShiftOut, UnavailabilityData } from '../../api';
import { UnifiedShiftAndUserAvailability } from '../UnifiedShiftAndUserAvailability';
import { SummaryCluster } from '../utils/calendarSummary';

export const HOUR_HEIGHT = 50;
export const DEFAULT_START_OFFSET = 7;

type Item = { time?: any; start_time?: any; end_time?: any; duration?: any };

type UnifiedData =
  | UnavailabilityData
  | EnrichedEventData
  | OrderData
  | UnifiedShiftAndUserAvailability
  | ShiftOut
  | NoteData
  | PrivateSlotOut
  | SummaryCluster;

export type ItemCssDimension = {
  top: number;
  height: number;
  width: number;
  left: number;
};
type ItemCss = {
  top: number;
  height: number;
  width: number;
  left: number;
} & { itemId: string };

const getItemStartTime = (item: Item) => {
  return item.time || item.start_time;
};

const getItemEndTime = (item: Item) => {
  // Not proud of it. but hey! fuck you!
  return item.end_time || item.time + item.duration;
};
const getItemWidth = (amountOfGroups: number) => {
  return 100 / amountOfGroups;
};
const getItemCss = (item: UnifiedData, leftShift: number, amountOfGroups: number): ItemCssDimension => {
  let top = HOUR_HEIGHT * (getItemStartTime(item) / 3600 - DEFAULT_START_OFFSET);

  if (top < 0) {
    top = 0;
  }
  return {
    top,
    height: ((getItemEndTime(item) - getItemStartTime(item)) / 3600) * HOUR_HEIGHT,
    width: getItemWidth(amountOfGroups),
    left: leftShift === 0 ? 0 : (100 / amountOfGroups) * leftShift,
  };
};
const getInternalShiftItemCss = (item: UnifiedShiftAndUserAvailability): ItemCssDimension => {
  const startTimeShift = item.userAvailability ? item.userAvailability.start_time! : 0;
  const startTime = (item.shift!.start_time - startTimeShift) / 3600;
  const height = (item.shift!.end_time - item.shift!.start_time) / 3600;
  let top = HOUR_HEIGHT * startTime;
  if (top < 0) {
    top = 0;
  }
  return {
    top,
    height: height * HOUR_HEIGHT,
    width: 100,
    left: 0,
  };
};
const getInternalOrderOrEventItemCss = (
  item: OrderData | EnrichedEventData,
  unifiedShiftAndUserAvailability: UnifiedShiftAndUserAvailability,
): ItemCssDimension => {
  const startTime = (item.time! - unifiedShiftAndUserAvailability.start_time) / 3600;
  const height = item.duration / 3600;

  let top = HOUR_HEIGHT * startTime;
  if (top < 0) {
    top = 0;
  }
  return {
    top,
    height: height * HOUR_HEIGHT,
    width: 100,
    left: 0,
  };
};

function eventsOverlap(eventA: Item, eventB: Item): boolean {
  return (
    getItemStartTime(eventA) < getItemEndTime(eventB) && // Event A's start time is before Event B's end time
    getItemEndTime(eventA) > getItemStartTime(eventB) // Event A's end time is after Event B's start time
  );
}

const isUnifiedShiftAndUserAvailability = (event: UnifiedData): event is UnifiedShiftAndUserAvailability => {
  return (event as UnifiedShiftAndUserAvailability).user_id !== undefined;
};

// Splits a start-sorted list into runs of items that overlap in time (touching end-to-start does not overlap).
const splitIntoOverlapClusters = (sortedItems: UnifiedData[]): UnifiedData[][] => {
  const clusters: UnifiedData[][] = [];
  let runningMaxEnd = 0;
  sortedItems.forEach((item) => {
    const currentCluster = clusters[clusters.length - 1];
    if (!currentCluster || getItemStartTime(item) >= runningMaxEnd) {
      clusters.push([item]);
      runningMaxEnd = getItemEndTime(item);
    } else {
      currentCluster.push(item);
      runningMaxEnd = Math.max(runningMaxEnd, getItemEndTime(item));
    }
  });
  return clusters;
};

export const getCalendarCellLocations = (
  orders: OrderData[],
  events: EnrichedEventData[],
  unavailabilities: UnavailabilityData[],
  shiftsAndUserAvailabilities: UnifiedShiftAndUserAvailability[],
  notes: NoteData[],
  privateSlotAvailabilities: PrivateSlotOut[],
  summaryClusters: SummaryCluster[] = [],
) => {
  const unified_list: UnifiedData[] = [
    ...orders,
    ...events,
    ...unavailabilities,
    ...shiftsAndUserAvailabilities,
    ...notes,
    ...privateSlotAvailabilities,
    ...summaryClusters,
  ];

  // Sort events by start time
  unified_list.sort((a, b) => getItemStartTime(a) - getItemStartTime(b));

  const cssResult: ItemCss[] = [];

  // Each overlap cluster is laid out on its own, so a crowded hour does not narrow the rest of the day.
  splitIntoOverlapClusters(unified_list).forEach((cluster) => {
    // Initialize groups
    const eventGroups: UnifiedData[][] = [];

    // Group overlapping events
    cluster.forEach((event) => {
      let added = false;
      for (const group of eventGroups) {
        // Check if the event overlaps with any event in the group
        if (group.every((groupEvent) => !eventsOverlap(event, groupEvent))) {
          group.push(event);
          added = true;
          break;
        }
      }
      if (!added) {
        eventGroups.push([event]);
      }
    });

    // Sort eventGroups based on the number of ShiftOut objects in each group
    // why? so shifts are aligned to the beging of the date cell
    eventGroups.sort((a, b) => {
      const shiftCountA = a.filter((event) => isUnifiedShiftAndUserAvailability(event)).length;
      const shiftCountB = b.filter((event) => isUnifiedShiftAndUserAvailability(event)).length;

      return shiftCountA === shiftCountB ? a.length - b.length : shiftCountB - shiftCountA;
    });

    // Calculate event positions within groups
    eventGroups.forEach((group, groupIndex) => {
      group.forEach((item) => {
        // groupIndex cause each group has it's own index and it's own vertical slot.
        // eventGroups.length to know how many slots
        cssResult.push({
          itemId: item.id || '',
          ...getItemCss(item, groupIndex, eventGroups.length),
        });
        if (isUnifiedShiftAndUserAvailability(item) && (item.shift || item.userAvailability)) {
          item.shiftCss = item.shift ? getInternalShiftItemCss(item) : undefined;
          const ordersCss: { id: string; css: ItemCssDimension }[] = [];
          item.orders?.forEach((x) => {
            ordersCss.push({ id: x.id, css: getInternalOrderOrEventItemCss(x, item) });
          });
          item.ordersCss = ordersCss;
          const eventsCss: { id: string; css: ItemCssDimension }[] = [];
          item.events?.forEach((x) => {
            eventsCss.push({ id: x.id, css: getInternalOrderOrEventItemCss(x, item) });
          });
          item.eventsCss = eventsCss;
        }
      });
    });
  });

  return cssResult;
};
